import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from "react";
import { VariableSizeList } from "react-window";
import { useMessageBus } from "@jti/fe-message-bus";
import { Button } from "../../../../components/Button";
import { StoreLocatorCard } from "../../../../components/StoreLocator/components/StoreLocatorCard";
import { useStoreLocatorContext } from "../../../../components/StoreLocator/hooks";
import { useDimension } from "../../../../hooks/useDimension";
import { useTranslations } from "../../../../providers/useTranslations";
import { StoreLocatorCount } from "../StoreLocatorCount";
import cardStyles from "../StoreLocatorCard/StoreLocatorCard.module.scss";
import styles from "./StoreLocatorCardList.module.scss";
const MOBILE_BREAKDOWN = 768;
const GUTTER_SIZE_VERTICAL = "var(--spacing-5, 20px)";
const GUTTER_SIZE_HORIZONTAL = "var(--des-dimension-gap-and-padding-general-near-large, 10px)";
const SingleRowForMapView = ({ activeStoreId, flotiqStores, storeDetailsPage }) => {
    const { onChooseStore, isNoMatchingFlotiqStores } = useStoreLocatorContext();
    const { __ } = useTranslations();
    const store = flotiqStores?.find(({ id }) => id === activeStoreId);
    if (!store) {
        return null;
    }
    return (_jsx(StoreLocatorCard, { isActive: false, store: store, storeDetailsPage: storeDetailsPage, isListView: true, children: onChooseStore && (_jsx(Button, { color: "primary", onClick: () => onChooseStore(store), disabled: isNoMatchingFlotiqStores, children: __("storelocator.select_store") })) }));
};
export const StoreLocatorCardList = ({ isMapView }) => {
    const { width } = useDimension();
    const { __ } = useTranslations();
    const messageBus = useMessageBus();
    const { activeStoreId, flotiqStores, handleSelectStore, storeDetailsPage, onChooseStore, isNoMatchingFlotiqStores } = useStoreLocatorContext();
    const resultsListRef = useRef(null);
    const listRef = useRef(null);
    const rowHeights = useRef({});
    const isMobileView = width < MOBILE_BREAKDOWN;
    const setRowHeight = (index, size) => {
        if (rowHeights.current[index] !== size) {
            rowHeights.current[index] = size;
            if (listRef.current) {
                listRef.current.resetAfterIndex(0);
            }
        }
    };
    const getItemSize = (index) => {
        return rowHeights.current[index] || 300;
    };
    const scrollToActiveElement = useCallback(() => {
        if (resultsListRef.current) {
            const activeIndex = flotiqStores?.findIndex(({ id }) => id === activeStoreId) || 0;
            if (activeIndex >= 0) {
                listRef.current?.scrollToItem(activeIndex);
                const activeElement = resultsListRef.current.querySelector(`.${cardStyles.resultCardActive}`);
                if (activeElement) {
                    activeElement.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
                }
            }
        }
    }, [activeStoreId, flotiqStores]);
    const publishStoreLocatorResultEvent = (storeId, storeName, storetype) => {
        messageBus.publish("tracking.event", {
            payload: {
                event_name: "store_locator_search_results_click",
                additionalData: {
                    store_id: storeId,
                    store_name: storeName,
                    store_type: storetype,
                },
            },
        });
    };
    const onClickCard = (storeId, storeName, storetype) => {
        publishStoreLocatorResultEvent(storeId, storeName, storetype);
        handleSelectStore(storeId);
        scrollToActiveElement();
    };
    const renderRow = ({ index, style }) => {
        const store = flotiqStores?.[index];
        if (!store) {
            return null;
        }
        return (_jsx("div", { style: style, children: _jsx("div", { style: { paddingBlockEnd: GUTTER_SIZE_VERTICAL, marginInlineEnd: GUTTER_SIZE_HORIZONTAL }, ref: (node) => {
                    if (node) {
                        const height = node.getBoundingClientRect().height;
                        setRowHeight(index, height);
                    }
                }, children: _jsx(StoreLocatorCard, { isActive: activeStoreId === store?.id, onSelectCard: onClickCard, store: store, storeDetailsPage: storeDetailsPage, isListView: true, children: onChooseStore && (_jsx(Button, { color: "primary", onClick: () => onChooseStore(store), disabled: isNoMatchingFlotiqStores, children: __("storelocator.select_store") })) }) }) }));
    };
    useEffect(() => {
        if (listRef.current) {
            rowHeights.current = {};
            listRef.current.resetAfterIndex(0);
        }
    }, [flotiqStores]);
    useEffect(() => {
        if (activeStoreId && (!isMobileView || !isMapView)) {
            scrollToActiveElement();
        }
    }, [activeStoreId, scrollToActiveElement, isMapView, isMobileView]);
    return (_jsx("div", { ref: resultsListRef, className: styles.listContainer, "data-testid": "storeLocatorCardList", children: isMapView && isMobileView ? (_jsx(SingleRowForMapView, { activeStoreId: activeStoreId, flotiqStores: flotiqStores, storeDetailsPage: storeDetailsPage })) : (_jsxs(_Fragment, { children: [!isMobileView && _jsx(StoreLocatorCount, {}), _jsx(VariableSizeList, { ref: listRef, direction: "vertical", height: 700, itemSize: getItemSize, itemCount: flotiqStores?.length || 0, width: "100%", children: renderRow })] })) }));
};
//# sourceMappingURL=StoreLocatorCardList.js.map